import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Handler from "../../../Helpers/Handler";
import ApiCall from "../../../Helpers/ApiCall";
import { formatIST } from "../../../Helpers/Utils";
import "../../../Assets/css/style.css";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";


const PAGE_LIMIT = 30;

const CommunityChat = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Expect: navigate("/user/community-chat", { state: { community_id, community_name } })
  const { community_id, community_name } = location.state || {};

  const currentUser = useSelector((s) => s.customer?.customer?.user);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState("");

  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMessages = useCallback(async (silent = false) => {
    if (!community_id) return;
    if (!silent) setLoading(true);
    const res = await Handler({
      method: "GET",
      url: `/communities/msg_list.php?community_id=${community_id}&page=1&limit=${PAGE_LIMIT}`,
    });
    if (!silent) setLoading(false);
    if (res.success) {
      setMessages(res.data?.list || []);
    }
  }, [community_id]);

  useEffect(() => {
    fetchMessages();
    // Poll every 8s
    pollRef.current = setInterval(() => fetchMessages(true), 8000);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendText = async () => {
    if (!text.trim()) return;
    setSending(true);
    setError("");
    const res = await Handler({
      method: "POST",
      url: "/communities/msg.php",
      data: { community_id, message: text.trim(), message_type: "TEXT" },
    });
    setSending(false);
    if (res.success) {
      setText("");
      await fetchMessages(true);
    } else {
      setError(res.message || "Failed to send message.");
    }
  };

  const sendImage = async (file) => {
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!allowed.includes(file.type)) {
      setError("Only JPEG, PNG, JPG, WEBP images allowed.");
      return;
    }
    setSending(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("community_id", community_id);
      formData.append("file", file);
      const res = await ApiCall({
        url: "/communities/msg.php",
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.success) {
        await fetchMessages(true);
      } else {
        setError(res.message || "Failed to upload image.");
      }
    } catch {
      setError("Image upload failed.");
    }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); }
  };

  // const BASE_URL = "http://localhost/business_community/";
  const BASE_URL = "https://qnexus.in/community/";

  return (
    <MainLayout>
      <FeedLayout>
        <div className="comm-page" style={{ paddingBottom: 0 }}>
          <div className="comm-header">
            <h2><i className="bx bx-chat" style={{ marginRight: 8 }} />{community_name || "Community Chat"}</h2>
            <button className="back-btn" onClick={() => navigate(-1)}>
              <i className="bx bx-arrow-back" /> Back
            </button>
          </div>

          {error && <div className="comm-alert error" style={{ marginBottom: 8 }}><i className="bx bx-error-circle" />{error}</div>}

          <div className="comm-chat-wrap">
            {/* Messages */}
            <div className="chat-messages-area">
              {loading ? (
                <div className="comm-spinner" />
              ) : messages.length === 0 ? (
                <div className="comm-empty">
                  <i className="bx bx-chat" />
                  <p>No messages yet. Say hello!</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isOwn = msg.sender_id === currentUser?.id;
                  return (
                    <div key={msg.id} className={`chat-bubble-wrap${isOwn ? " own" : ""}`}>
                      <div className="chat-bubble-avatar">
                        {(msg.sender_name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="chat-bubble">
                        {!isOwn && <div className="bubble-sender">{msg.sender_name}</div>}
                        {msg.message_type === "IMAGE" ? (
                          <img
                            src={BASE_URL + msg.message}
                            alt="shared"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        ) : (
                          <span>{msg.message}</span>
                        )}
                        <div className="bubble-time">
                          {new Date(msg.created_at).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input Bar */}
            <div className="chat-input-bar">
              {/* Image upload */}
              <button
                className="chat-icon-btn img-upload"
                title="Send image"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending}
              >
                <i className="bx bx-image-add" />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/webp"
                  onChange={(e) => { sendImage(e.target.files[0]); e.target.value = ""; }}
                />
              </button>

              <input
                type="text"
                placeholder="Type a message… (Enter to send)"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending}
                maxLength={1000}
              />

              <button
                className="chat-icon-btn send"
                onClick={sendText}
                disabled={sending || !text.trim()}
                title="Send"
              >
                {sending ? <i className="bx bx-loader-alt bx-spin" /> : <i className="bx bx-send" />}
              </button>
            </div>
          </div>
        </div>
      </FeedLayout>
    </MainLayout>

  );
};

export default CommunityChat;